// The participant's goal register (their NDIS goals): one status, the start and outcome dates, and
// a rating. Every row opens for editing and has its own actions.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CheckCircle2, Pencil, Plus, Target, Trash2, XCircle } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import { DataTable, FilterChips, OverflowMenu, SearchInput, StatusPill, fmtDate } from '../../components/ui';
import { useClientRecord, ClientFrame } from './ClientLayout';
import { dayOf, todayIso } from './clientShared';

// One status from the three dates. Achieved wins over closed (it is the outcome).
export const goalStatus = (g) => (g.achieved_at ? 'achieved' : g.closed_at ? 'closed' : 'active');
export const GOAL_STATUS = {
  active: { label: 'Active', tone: 'info' },
  achieved: { label: 'Achieved', tone: 'success' },
  closed: { label: 'Closed', tone: 'neutral' },
};
const STATUS_ORDER = { active: 0, achieved: 1, closed: 2 };

// The body the goal update expects: every field, so nothing is cleared by accident.
export const goalPayload = (g, patch = {}) => ({
  name: g.name || '',
  description: g.description || '',
  commencedAt: dayOf(g.commenced_at) || null,
  achievedAt: dayOf(g.achieved_at) || null,
  closedAt: dayOf(g.closed_at) || null,
  rating: g.rating ?? null,
  ...patch,
});

export default function ClientGoals() {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const record = useClientRecord(id);

  const [goals, setGoals] = useState([]);
  const [state, setState] = useState({ loading: true, error: null });
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');

  const load = useCallback(() => {
    setState({ loading: true, error: null });
    api.get(`/clients/${id}/goals`)
      .then((r) => { setGoals(Array.isArray(r.data) ? r.data : []); setState({ loading: false, error: null }); })
      .catch((e) => setState({ loading: false, error: e.response?.data?.message || 'We couldn’t load the goals. Check your connection, then try again.' }));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const update = async (g, patch, done) => {
    try {
      const { data } = await api.put(`/clients/${id}/goals/${g.id}`, goalPayload(g, patch));
      setGoals((gs) => gs.map((x) => (x.id === g.id ? { ...x, ...data } : x)));
      toast.success(done);
    } catch (e) { toast.error(e.response?.data?.message || 'Could not update the goal'); }
  };

  const markAchieved = (g) => update(g, { achievedAt: todayIso() }, 'Goal marked achieved');
  const closeGoal = async (g) => {
    if (!await confirm(`“${g.name}” is closed today without being marked achieved. You can reopen it from Edit.`, { title: 'Close this goal?', confirmLabel: 'Close goal' })) return;
    update(g, { closedAt: todayIso() }, 'Goal closed');
  };
  const remove = async (g) => {
    if (!await confirm(`“${g.name}” is deleted for good.`, { title: 'Delete this goal?', danger: true, confirmLabel: 'Delete goal' })) return;
    try {
      await api.delete(`/clients/${id}/goals/${g.id}`);
      setGoals((gs) => gs.filter((x) => x.id !== g.id));
      toast.success('Goal deleted');
    } catch (e) { toast.error(e.response?.data?.message || 'Could not delete the goal'); }
  };

  const counts = useMemo(() => goals.reduce((m, g) => { const s = goalStatus(g); m[s] = (m[s] || 0) + 1; return m; }, {}), [goals]);
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return goals
      .filter((g) => !q || `${g.name || ''} ${g.description || ''}`.toLowerCase().includes(q))
      .filter((g) => status === 'all' || goalStatus(g) === status)
      .sort((a, b) => STATUS_ORDER[goalStatus(a)] - STATUS_ORDER[goalStatus(b)]
        || String(dayOf(b.commenced_at)).localeCompare(String(dayOf(a.commenced_at))));
  }, [goals, search, status]);

  const openGoal = (g) => navigate(`/clients/${id}/goals/${g.id}`);

  const columns = [
    {
      key: 'name', header: 'Goal', sortable: true,
      render: (g) => (
        <div className="min-w-0 max-w-[36rem]">
          <Link to={`/clients/${id}/goals/${g.id}`} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}
            className="font-medium text-slate-800 hover:text-primary-700">{g.name}</Link>
          {g.description && <p className="text-[12.5px] text-slate-500 truncate">{g.description}</p>}
        </div>
      ),
    },
    { key: 'status', header: 'Status', sortable: true, sortValue: (g) => STATUS_ORDER[goalStatus(g)], render: (g) => <StatusPill status={goalStatus(g)} map={GOAL_STATUS} /> },
    { key: 'commenced_at', header: 'Started', sortable: true, sortValue: (g) => dayOf(g.commenced_at) || null, render: (g) => <span className="tabular-nums text-slate-600 whitespace-nowrap">{g.commenced_at ? fmtDate(dayOf(g.commenced_at)) : '—'}</span> },
    {
      key: 'outcome', header: 'Outcome date', sortable: true, sortValue: (g) => dayOf(g.achieved_at || g.closed_at) || null,
      render: (g) => { const d = g.achieved_at || g.closed_at; return <span className="tabular-nums text-slate-600 whitespace-nowrap">{d ? fmtDate(dayOf(d)) : '—'}</span>; },
    },
    {
      key: 'rating', header: 'Rating', sortable: true, align: 'right', sortValue: (g) => (g.rating == null ? null : Number(g.rating)),
      render: (g) => <span className="tabular-nums text-slate-700 whitespace-nowrap">{g.rating == null ? '—' : `${g.rating} / 5`}</span>,
    },
    {
      key: 'actions', header: <span className="sr-only">Actions</span>, align: 'right', width: 48,
      render: (g) => {
        const s = goalStatus(g);
        return (
          <span className="inline-flex" onKeyDown={(e) => e.stopPropagation()}>
            <OverflowMenu label={`Actions for ${g.name}`} items={[
              { label: 'Edit', icon: Pencil, onClick: () => openGoal(g) },
              s !== 'achieved' && { label: 'Mark achieved', icon: CheckCircle2, onClick: () => markAchieved(g) },
              s === 'active' && { label: 'Close', icon: XCircle, onClick: () => closeGoal(g) },
              { divider: true },
              { label: 'Delete', icon: Trash2, danger: true, onClick: () => remove(g) },
            ]} />
          </span>
        );
      },
    },
  ];

  const newGoal = <Link to={`/clients/${id}/goals/new`} className="btn-primary"><Plus size={15} aria-hidden /> New goal</Link>;
  const filtering = !!(search.trim() || status !== 'all');

  return (
    <ClientFrame record={record}>
      <p className="text-[13px] text-slate-500">
        The participant’s goal register. Goals inside a care plan, with progress tracking, are on the{' '}
        <Link to={`/clients/${id}/care-plan`} className="text-primary-700 hover:underline">Care plan tab</Link>.
      </p>
      <section aria-label="Goals" className="bg-white border border-slate-200 rounded-xl">
        <div className="flex flex-wrap items-center gap-2 px-3 sm:px-4 py-3 border-b border-slate-100">
          <SearchInput value={search} onChange={setSearch} placeholder="Search goals" className="w-full sm:w-64" />
          <FilterChips multi={false} value={status} onChange={(v) => setStatus(v || 'all')} ariaLabel="Filter goals by status"
            options={[
              { value: 'all', label: 'All', count: goals.length },
              { value: 'active', label: 'Active', count: counts.active || 0 },
              { value: 'achieved', label: 'Achieved', count: counts.achieved || 0 },
              { value: 'closed', label: 'Closed', count: counts.closed || 0 },
            ]} />
          <div className="ml-auto">{newGoal}</div>
        </div>
        <DataTable columns={columns} rows={rows} rowKey="id" loading={state.loading} error={state.error} onRetry={load}
          onRowClick={openGoal} maxHeight="70vh" label="Goals"
          empty={filtering
            ? { icon: Target, title: 'No goals match', hint: 'Try another search or status.' }
            : { icon: Target, title: 'No goals yet', hint: 'Add the participant’s NDIS plan goals so shift notes can report progress against them.', action: newGoal }} />
      </section>
    </ClientFrame>
  );
}
